import type { WebPageFeatureReturnDto } from '@terra/shared-contracts-web-pages';

export type FeatureCoverage = 'covered' | 'blocked' | 'uncovered';

export type FeatureTypeColorPalette = 'blue' | 'green' | 'orange' | 'red';

export const getFeatureCoverage = (
  feature: WebPageFeatureReturnDto,
): FeatureCoverage => {
  if (feature.isFeatureExecuted) {
    return 'covered';
  }

  if (feature.executionPolicy === 'block') {
    return 'blocked';
  }

  return 'uncovered';
};

export const isFeatureOverrideRunning = (
  feature: WebPageFeatureReturnDto,
): boolean =>
  feature.lastRunIsPolicyOverride &&
  (feature.latestCrawlerSessionStatus === 'RUNNING' ||
    feature.latestCrawlerSessionStatus === 'ABORTING');

export const canUnblockFeature = ({
  feature,
  isOverrideInProgress,
}: {
  feature: WebPageFeatureReturnDto;
  isOverrideInProgress: boolean;
}): boolean =>
  feature.executionPolicy === 'block' &&
  !feature.isFeatureExecuted &&
  !isOverrideInProgress;

export const featureCoverageDotColor: Record<FeatureCoverage, string> = {
  covered: 'green.9',
  blocked: 'yellow.9',
  uncovered: 'gray.8',
};

export const featureTypeColorPalette: Record<
  WebPageFeatureReturnDto['featureType'],
  FeatureTypeColorPalette
> = {
  get: 'blue',
  create: 'green',
  update: 'orange',
  delete: 'red',
};

export const parseReproduceSteps = (reproduce: string | null): string[] => {
  if (!reproduce?.trim()) {
    return [];
  }

  const numberedSteps = reproduce
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.replace(/^\d+[.)]\s*/, '').trim())
    .filter(Boolean);

  if (numberedSteps.length > 1) {
    return numberedSteps;
  }

  const inlineSteps = reproduce
    .split(/(?<!\d)(?=\d+[.)]\s)/)
    .map((part) => part.replace(/^\d+[.)]\s*/, '').trim())
    .filter(Boolean);

  if (inlineSteps.length > 1) {
    return inlineSteps;
  }

  return numberedSteps.length > 0 ? numberedSteps : [reproduce.trim()];
};
